import React__default from 'react';
import { Menu, Popover, OverlayTriggerStateContext, MenuItem, Text, Separator, MenuTrigger } from 'react-aria-components';
import { isButtonComponent } from '../utils/isButtonComponent.js';
import './ActionMenu.scss';

/**
 * A wrapper around React Aria's MenuTrigger and Menu components for creating action menus.
 *
 * ```tsx
 * <ActionMenuTrigger>
 *   <Button>Actions</Button>
 *   <ActionMenu>
 *     <ActionMenuItem label="Edit" onAction={() => alert('Edit')} />
 *     <ActionMenuItem label="Delete" onAction={() => alert('Delete')} />
 *   </ActionMenu>
 * </ActionMenuTrigger>
 * ```
 */
const ActionMenu = ({ children, inPopover = true, placement = 'bottom end', offset = 4, crossOffset, shouldFlip = true, className, selectionMode, selectedKeys, onSelectionChange, contentAboveMenu, contentBelowMenu, }) => {
    const noop = React__default.useCallback(() => { }, []);
    const MenuComponent = (React__default.createElement(React__default.Fragment, null,
        contentAboveMenu,
        React__default.createElement(Menu, { selectionMode: selectionMode, selectedKeys: selectedKeys, onSelectionChange: onSelectionChange, className: 'action-menu-container--rev', shouldFocusWrap: false, onClose: inPopover ? undefined : noop }, children),
        contentBelowMenu));
    if (inPopover) {
        return (React__default.createElement(Popover, { "data-rev-component": 'action-menu', placement: placement, offset: offset, crossOffset: crossOffset, shouldFlip: shouldFlip, className: className }, MenuComponent));
    }
    /* Prevent MenuItem selection from closing a parent Dialog/Modal
       (e.g. SearchInput inside a Popup). The onClose noop on Menu
       blocks the useMenuItem close path; the null context provider
       blocks any overlay dismiss propagation via OverlayTriggerStateContext. */
    return (React__default.createElement(OverlayTriggerStateContext.Provider, { value: null },
        React__default.createElement("div", { "data-rev-component": 'action-menu', className: className }, MenuComponent)));
};
const CheckmarkIcon = () => {
    return (React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '24', height: '24', viewBox: '0 0 24 24', fill: 'none', className: 'action-menu-item-checkmark--rev', "aria-hidden": 'true' },
        React__default.createElement("path", { d: 'M7 13L10.75 17L17 7', stroke: 'black', strokeWidth: '1.5', strokeLinecap: 'round', strokeLinejoin: 'round' })));
};
/**
 * A menu item component for use within ActionMenu.
 */
const ActionMenuItem = ({ label, description, leadingAccessory, trailingAccessory, onAction, disabled = false, destructive = false, className, id, href, ariaLabel, }) => {
    return (React__default.createElement(MenuItem, { "data-rev-component": 'action-menu-item', onAction: onAction, isDisabled: disabled, "data-rev-destructive": destructive, "data-rev-has-leading-accessory": !!leadingAccessory, className: className || '', "aria-label": ariaLabel, href: href, id: id }, ({ selectionMode }) => (React__default.createElement(React__default.Fragment, null,
        leadingAccessory && (React__default.createElement("div", { className: 'action-menu-item-accessory--rev action-menu-item-accessory-leading--rev' }, leadingAccessory)),
        React__default.createElement(Text, { slot: 'label', className: 'action-menu-item-label--rev' }, label),
        description && (React__default.createElement(Text, { slot: 'description', className: 'action-menu-item-description--rev' }, description)),
        trailingAccessory ||
            (!!selectionMode && (React__default.createElement("div", { className: 'action-menu-item-accessory--rev action-menu-item-trailing-accessory--rev' }, selectionMode ? React__default.createElement(CheckmarkIcon, null) : trailingAccessory)))))));
};
const ActionMenuItemDivider = () => React__default.createElement(Separator, { "data-rev-component": 'action-menu-divider' });
/**
 * A trigger component that wraps a Button and an ActionMenu.
 *
 * ```tsx
 * <ActionMenuTrigger>
 *   <Button>Actions</Button>
 *   <ActionMenu>
 *     <ActionMenuItem label="Edit" onAction={() => alert('Edit')} />
 *     <ActionMenuItem label="Delete" onAction={() => alert('Delete')} />
 *   </ActionMenu>
 * </ActionMenuTrigger>
 * ```
 */
const ActionMenuTrigger = ({ children }) => {
    // Check if the first child is a Button component and
    // enhance it with aria-haspopup if it is
    const enhancedChildren = React__default.Children.map(children, (child, index) => {
        // Only process the first child (the trigger element)
        if (index === 0 && React__default.isValidElement(child)) {
            if (isButtonComponent(child)) {
                // Magically add the aria-haspopup attribute
                return React__default.cloneElement(child, {
                    ...child.props,
                    ariaHaspopup: 'menu',
                });
            }
        }
        return child;
    });
    return React__default.createElement(MenuTrigger, null, enhancedChildren);
};

export { ActionMenu, ActionMenuItem, ActionMenuItemDivider, ActionMenuTrigger };
//# sourceMappingURL=ActionMenu.js.map
